import { $getRoot, LexicalNode, TextNode } from 'lexical';
import { getTextStylesFromBrandStyle, tryToGetBrandColorRefByHEX } from 'modules/BrandDefinition';
import { $isCustomListNode } from 'modules/Lexical/nodes/CustomListNode';
import { FontStyle } from 'const';
import * as Constants from 'const';
import * as Models from 'models';
import { $getTextNodeStyleValueForProperty } from '../../lexicalUtils';
import { $isCustomHeadingNode } from '../../nodes/CustomHeadingNode';
import { $isCustomListItemNode } from '../../nodes/CustomListItemNode';
import { $isCustomParagraphNode } from '../../nodes/CustomParagraphNode';
import { $doAllNodesHaveBulletColorHex } from '../CustomListPlugin/utils';
import { FontPluginStyle } from '../FontPlugin/style';

type TextFormatExpected = {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
};

const getExpectedTextFormat = (
  characterStyleName: string | null | undefined,
  brandFont: Models.BrandFont | undefined,
): TextFormatExpected | null => {
  if (!brandFont) {
    return null;
  }

  // If characterStyleName is empty string, treat it as "Regular" (no bold/italic)
  if (!characterStyleName) {
    return { isBold: false, isItalic: false, isUnderline: false };
  }

  const charStyle = brandFont.characterStyles.find(cs => cs.name === characterStyleName);
  if (!charStyle?.if) {
    return null;
  }

  // Map FontStyle postfix to expected bold/italic flags
  const formatMap: Record<string, TextFormatExpected> = {
    [FontStyle.REGULAR]: { isBold: false, isItalic: false, isUnderline: false },
    [FontStyle.ITALIC]: { isBold: false, isItalic: true, isUnderline: false },
    [FontStyle.STRONG]: { isBold: true, isItalic: false, isUnderline: false },
    [FontStyle.ITALIC_STRONG]: { isBold: true, isItalic: true, isUnderline: false },
  };

  return formatMap[charStyle.if] || null;
};

const $doAllNodesHaveMatchingTextFormat = (
  allTextNodes: TextNode[],
  expectedBold: boolean,
  expectedItalic: boolean,
  expectedUnderline: boolean,
): boolean => {
  let allMatch = true;
  allTextNodes.forEach((node) => {
    // Check CSS style properties for bold/italic
    const fontWeight = $getTextNodeStyleValueForProperty(node, FontPluginStyle.FONT_WEIGHT, '');
    const fontStyle = $getTextNodeStyleValueForProperty(node, FontPluginStyle.FONT_STYLE, '');

    // Bold is when fontWeight >= 700
    const isBold = fontWeight ? parseInt(fontWeight, 10) >= 700 : false;
    // Italic is when fontStyle contains "italic"
    const isItalic = fontStyle?.toLowerCase().includes('italic') ?? false;
    // Underline is built-in Lexical format
    const isUnderline = node.hasFormat('underline');

    if (!fontWeight && !fontStyle && !isUnderline) {
      return;
    }

    // Check if formatting matches expected
    if (
      isBold !== expectedBold ||
      isItalic !== expectedItalic ||
      isUnderline !== expectedUnderline
    ) {
      allMatch = false;
    }
  });

  return allMatch;
};

const $doAllNodesHaveFontSize = (allTextNodes: TextNode[], fontSize: number): boolean => {
  if (!fontSize) {
    return true;
  }
  let allMatch = true;
  const expectedFontSize = `${fontSize}px`;
  allTextNodes.forEach((node) => {
    const nodeFontSize = $getTextNodeStyleValueForProperty(node, FontPluginStyle.FONT_SIZE, '');
    if (!nodeFontSize) {
      return;
    }
    if (nodeFontSize !== expectedFontSize) {
      allMatch = false;
    }
  });

  return allMatch;
};

const $doAllNodesHaveBrandColorName = (allTextNodes: TextNode[], colorName: string): boolean => {
  if (!colorName) {
    return true;
  }
  let allMatch = true;
  allTextNodes.forEach((node) => {
    const nodeColorName = $getTextNodeStyleValueForProperty(node, FontPluginStyle.BRAND_COLOR_NAME, '');
    if (nodeColorName !== colorName) {
      allMatch = false;
    }
  });

  return allMatch;
};

const $doAllNodesHaveBrandFontName = (allTextNodes: TextNode[], fontName: string): boolean => {
  if (!fontName) {
    return true;
  }
  let allMatch = true;
  allTextNodes.forEach((node) => {
    const nodeFontName = $getTextNodeStyleValueForProperty(node, FontPluginStyle.BRAND_FONT_NAME, '');
    if (!nodeFontName) {
      return;
    }
    if (nodeFontName !== fontName) {
      allMatch = false;
    }
  });

  return allMatch;
};

const $doAllNodesHaveBrandFontCharStyle = (allTextNodes: TextNode[], charStyle: string): boolean => {
  if (!charStyle) {
    return true;
  }
  let allMatch = true;
  allTextNodes.forEach((node) => {
    const nodeCharStyle = $getTextNodeStyleValueForProperty(
      node,
      FontPluginStyle.BRAND_FONT_CHARACTER_STYLE_NAME,
      '',
    );
    if (!nodeCharStyle) {
      return;
    }
    if (nodeCharStyle !== charStyle) {
      allMatch = false;
    }
  });

  return allMatch;
};

const $doAllNodesHaveLineHeight = (rootChildren: LexicalNode[], lineHeight: number): boolean => {
  let allMatch = true;
  rootChildren.forEach((node) => {
    if ($isCustomParagraphNode(node) || $isCustomHeadingNode(node) || $isCustomListItemNode(node)) {
      const nodeLineHeight = node.getLineHeight();
      if (nodeLineHeight === undefined) {
        return;
      }
      if (nodeLineHeight !== lineHeight) {
        allMatch = false;
      }
    }
  });

  return allMatch;
};

const $doAllNodesHaveHorizontalAlignment = (rootChildren: LexicalNode[], textAlign: string): boolean => {
  const normalizeAlignment = (value: string | undefined): string => {
    if (!value) {
      return 'left';
    }

    return value;
  };

  const expectedAlignment = normalizeAlignment(textAlign);
  let allMatch = true;
  rootChildren.forEach((node) => {
    if ($isCustomParagraphNode(node) || $isCustomHeadingNode(node)) {
      const nodeAlign = normalizeAlignment(node.getFormatType());
      if (nodeAlign !== expectedAlignment) {
        allMatch = false;
      }
    }
  });

  return allMatch;
};

const $doAllHeadingNodesHaveTag = (rootChildren: LexicalNode[], tag: string): boolean => {
  let allMatch = true;
  rootChildren.forEach((node) => {
    if ($isCustomHeadingNode(node)) {
      const nodeTag = node.getTag();
      if (nodeTag !== tag) {
        allMatch = false;
      }
    }
  });

  return allMatch;
};

const $doAllTextNodesMatchStyle = (
  allTextNodes: TextNode[],
  fontSize: number,
  fontColorName: string,
  fontFamily: string,
  characterStyle: string,
  expectedTextFormat?: TextFormatExpected | null,
): boolean => {
  const isFontSizeSame = $doAllNodesHaveFontSize(allTextNodes, fontSize);
  const isColorSame = $doAllNodesHaveBrandColorName(allTextNodes, fontColorName);
  const isFontNameSame = $doAllNodesHaveBrandFontName(allTextNodes, fontFamily);
  const isCharStyleSame = $doAllNodesHaveBrandFontCharStyle(allTextNodes, characterStyle);

  let isTextFormatSame = true;
  // Always check text format if provided
  if (expectedTextFormat !== null && expectedTextFormat !== undefined) {
    isTextFormatSame = $doAllNodesHaveMatchingTextFormat(
      allTextNodes,
      expectedTextFormat.isBold,
      expectedTextFormat.isItalic,
      expectedTextFormat.isUnderline,
    );
  }

  return isFontSizeSame && isColorSame && isFontNameSame && isCharStyleSame && isTextFormatSame;
};

const $doAllBlockNodesMatchStyle = (
  rootChildren: LexicalNode[],
  lineHeight: number,
  textAlign: string,
  headingTag?: string,
): boolean => {
  const hasBulletList = rootChildren.some(node => $isCustomListNode(node));
  if (hasBulletList) {
    return false;
  }

  const isLineHeightSame = $doAllNodesHaveLineHeight(rootChildren, lineHeight);
  if (!isLineHeightSame) {
    return false;
  }
  const isTextAlignSame = $doAllNodesHaveHorizontalAlignment(rootChildren, textAlign);
  if (!isTextAlignSame) {
    return false;
  }
  const isHeadingTagSame = headingTag ? $doAllHeadingNodesHaveTag(rootChildren, headingTag) : true;
  if (!isHeadingTagSame) {
    return false;
  }

  return isLineHeightSame && isTextAlignSame && isHeadingTagSame;
};

const $doAllListNodesMatchStyle = (
  rootChildren: LexicalNode[],
  lineHeight: number,
  textAlign: string,
  bulletColorHex: string,
): boolean => {
  const isLineHeightSame = $doAllNodesHaveLineHeight(rootChildren, lineHeight);
  if (!isLineHeightSame) {
    return false;
  }
  const isTextAlignSame = $doAllNodesHaveHorizontalAlignment(rootChildren, textAlign);
  if (!isTextAlignSame) {
    return false;
  }
  const isBulletColorSame = $doAllNodesHaveBulletColorHex(bulletColorHex);
  if (!isBulletColorSame) {
    return false;
  }

  return isLineHeightSame && isTextAlignSame && isBulletColorSame;
};

const getBrandFont = (fontName: string, fonts: Models.BrandFontsList): Models.BrandFont | undefined => {
  if (!fontName || !fonts) {
    return undefined;
  }

  const fontMap = fonts.find((font: Models.BrandFontMap) => font.get('name') === fontName);
  if (!fontMap) {
    return undefined;
  }

  // Convert Immutable Map to BrandFont object
  const charStyles = fontMap.get('characterStyles');

  return {
    id: fontMap.get('id') || '',
    name: fontMap.get('name') || '',
    bulletColor: fontMap.get('bulletColor') || '',
    characterStyles: Array.isArray(charStyles) ? charStyles : [],
    fontFamily: fontMap.get('fontFamily') || '',
    fallbackFonts: fontMap.get('fallbackFonts'),
    fontColor: fontMap.get('fontColor'),
    fontStyle: fontMap.get('fontStyle'),
    fontWeight: fontMap.get('fontWeight'),
  };
};

export type CheckSource = 'text' | 'paragraph' | 'heading' | 'list';

const CHECK_SOURCES: CheckSource[] = ['text', 'paragraph', 'heading', 'list'];

export const areBrandStyleChangedMapsEqual = (
  left: Partial<Record<CheckSource, boolean>>,
  right: Partial<Record<CheckSource, boolean>>,
): boolean => CHECK_SOURCES.every(source => Boolean(left[source]) === Boolean(right[source]));

type BuildBrandStyleChangedInput = {
  brandStyle: Models.BrandStyleMap;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
};

export const $buildBrandStyleChangedBySource = (
  { brandStyle, colors, fonts }: BuildBrandStyleChangedInput): Partial<Record<CheckSource, boolean>> => {
  const {
    fontSize,
    fontColor,
    fontFamily,
    characterStyle,
    lineHeight,
    textAlign,
    bulletColor,
  } = getTextStylesFromBrandStyle(brandStyle, colors, fonts);

  const headingTag: string | undefined = brandStyle.get('tag');
  const fontColorRef = tryToGetBrandColorRefByHEX(colors, fontColor);
  const bulletColorRef = tryToGetBrandColorRefByHEX(colors, bulletColor);
  const fontColorName = fontColorRef?.name ?? '';
  const bulletColorHex = bulletColorRef?.HEX ?? '';
  const shouldBeList = textAlign === Constants.TextHorizontalAlignmentType.UNORDERED_LIST;
  const brandFont = getBrandFont(fontFamily, fonts);
  const expectedTextFormat = getExpectedTextFormat(characterStyle, brandFont);

  const nextBrandStyleChangedBySource: Partial<Record<CheckSource, boolean>> = {};

  const root = $getRoot();
  const allTextNodes = root.getAllTextNodes();
  const rootChildren = root.getChildren();

  const isTextStyleNotSame = !$doAllTextNodesMatchStyle(
    allTextNodes,
    fontSize,
    fontColorName,
    fontFamily,
    characterStyle,
    expectedTextFormat,
  );
  nextBrandStyleChangedBySource.text = isTextStyleNotSame;

  if (shouldBeList) {
    const isListStyleNotSame = !$doAllListNodesMatchStyle(rootChildren, lineHeight, textAlign, bulletColorHex);
    nextBrandStyleChangedBySource.list = isListStyleNotSame;

    return nextBrandStyleChangedBySource;
  }

  const isParagraphStyleNotSame = !$doAllBlockNodesMatchStyle(rootChildren, lineHeight, textAlign);
  nextBrandStyleChangedBySource.paragraph = isParagraphStyleNotSame;

  const isHeadingStyleNotSame = !$doAllBlockNodesMatchStyle(rootChildren, lineHeight, textAlign, headingTag);
  nextBrandStyleChangedBySource.heading = isHeadingStyleNotSame;

  return nextBrandStyleChangedBySource;
};
